import dayjs from 'dayjs';
import type { MatchResult, PreMatchWidgetMatch, PreMatchWidgetTeam, StandingZone } from './types';

type CountdownLabels = {
  day: string;
  days: string;
  hourShort: string;
  hoursShort: string;
  minuteShort: string;
  minutesShort: string;
};

const DEFAULT_COUNTDOWN_LABELS: CountdownLabels = {
  day: 'day',
  days: 'days',
  hourShort: 'hr',
  hoursShort: 'hrs',
  minuteShort: 'min',
  minutesShort: 'mins',
};

const STANDING_ZONE_COLOR: Record<Exclude<StandingZone, 'neutral'>, string> = {
  champions: 'var(--pmw-warning)',
  europa: 'var(--pmw-accent)',
  conference: 'var(--pmw-success)',
  relegation: 'var(--pmw-danger)',
};

export const formatDisplayDate = (date: string, includeTime = true) =>
  dayjs(date).format(includeTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY');

export const formatScore = (score?: string) => score?.replace(/\s*-\s*/g, ' : ');

export const normalizeRoundValue = (round?: string | number | null) => {
  if (round === null || round === undefined) return null;

  const normalizedRound = `${round}`.trim();

  return normalizedRound ? normalizedRound : null;
};

export const formatRoundLabel = (round: string | null | undefined, roundLabel: string) => {
  const normalizedRound = normalizeRoundValue(round);

  if (!normalizedRound) return null;

  if (/\p{L}/u.test(normalizedRound)) {
    return normalizedRound;
  }

  return `${roundLabel} ${normalizedRound}`;
};

export const getCountdown = (date: string, labels: CountdownLabels = DEFAULT_COUNTDOWN_LABELS) => {
  const now = dayjs();
  const target = dayjs(date);
  const days = target.diff(now, 'day');

  if (days >= 1) {
    return {
      value: `${days}`,
      label: days === 1 ? labels.day : labels.days,
    };
  }

  const hours = Math.max(target.diff(now, 'hour'), 0);

  if (hours >= 1) {
    return {
      value: `${hours}`,
      label: hours === 1 ? labels.hourShort : labels.hoursShort,
    };
  }

  const minutes = Math.max(target.diff(now, 'minute'), 0);

  return {
    value: `${minutes}`,
    label: minutes === 1 ? labels.minuteShort : labels.minutesShort,
  };
};

const parseScore = (score?: string) => {
  if (!score) return null;

  const values = score.match(/\d+/g);

  if (!values || values.length < 2) return null;

  return [Number(values[0]), Number(values[1])] as const;
};

const normalizeTeamIdentifier = (value?: string | null) => {
  const normalizedValue = value
    ?.trim()
    .toLowerCase()
    .replace(/\b(f\.?c\.?|cf|afc)\b/g, '')
    .replace(/[^\p{L}\p{N}]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return normalizedValue || null;
};

export const matchesTeamIdentifier = (
  team: Pick<PreMatchWidgetTeam, 'name' | 'shortName'>,
  matchTeamName: string,
  matchTeamShortName?: string | null
) => {
  const teamIdentifiers = [team.name, team.shortName].map(normalizeTeamIdentifier).filter(Boolean);
  const matchIdentifiers = [matchTeamName, matchTeamShortName].map(normalizeTeamIdentifier).filter(Boolean);

  return matchIdentifiers.some((matchIdentifier) =>
    teamIdentifiers.some((teamIdentifier) => matchIdentifier === teamIdentifier)
  );
};

const deriveResultForTrackedTeam = (
  match: PreMatchWidgetMatch,
  team: Pick<PreMatchWidgetTeam, 'name' | 'shortName'>
): MatchResult | null => {
  const parsedScore = parseScore(match.score);

  if (!parsedScore) return null;

  const [homeScore, awayScore] = parsedScore;
  const isHomeTeam = matchesTeamIdentifier(team, match.homeTeam, match.homeTeamShortName);
  const isAwayTeam = matchesTeamIdentifier(team, match.awayTeam, match.awayTeamShortName);

  if (!isHomeTeam && !isAwayTeam) return null;

  if (homeScore === awayScore) return 'D';

  if (isHomeTeam) {
    return homeScore > awayScore ? 'W' : 'L';
  }

  return awayScore > homeScore ? 'W' : 'L';
};

export const getResultForTrackedTeam = (
  match: PreMatchWidgetMatch,
  team: Pick<PreMatchWidgetTeam, 'name' | 'shortName'>
): MatchResult | null => match.resultForTrackedTeam ?? deriveResultForTrackedTeam(match, team);

export const getResultBadgeStyles = (result: MatchResult) => {
  if (result === 'W') {
    return {
      backgroundColor: 'var(--pmw-success)',
      color: 'var(--pmw-panel-strong)',
    };
  }

  if (result === 'L') {
    return {
      backgroundColor: 'var(--pmw-danger)',
      color: 'var(--pmw-text)',
    };
  }

  return {
    backgroundColor: 'var(--pmw-warning)',
    color: 'var(--pmw-panel-strong)',
  };
};

export const getStandingZoneColor = (zone: StandingZone) => {
  if (zone === 'neutral') return 'transparent';

  return STANDING_ZONE_COLOR[zone];
};
